import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ───────────────── CONSTANTS ───────────────── */

const PRODUCTS_COLLECTION = "StoreProducts";

/* ───────────────── TYPES ───────────────── */

export type Product = {
  listingId: string;
  brandId: string;
  hostId: string;

  name?: string;
  productId?: number | null;

  stock?: number;
  available?: boolean;
  rate?: number;

  photos?: string[];
  videoUrl?: string;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  isDraft?: boolean;

  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type ProductState = {
  filteredListings: any;
  products: Product[];
  filteredProducts: Product[];
  filters: {
    city: string | null;
    country: string | null;
  };
  currentProduct: Product | null;
  unsubscribe?: () => void;
  loading: boolean;
  error: string | null;
  brandProducts: Product[];
  brandProductsId: string | null;
  brandProductsLoading: boolean;
  brandUnsubscribe?: () => void;
};

const initialState: ProductState = {
  products: [],
  currentProduct: null,
  unsubscribe: undefined,
  loading: false,
  error: null,
  filteredProducts: [],
  filters: {
    city: null,
    country: null,
  },
  brandProducts: [],
  brandProductsId: null,
  brandProductsLoading: false,
  filteredListings: []
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseProduct = (
  id: string,
  raw: Record<string, any>
): Product => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    listingId: id,
    brandId: raw.brandId ?? "",
    hostId: raw.hostId ?? "",

    name: raw.name ?? "",
    productId: raw.productId ?? null,

    stock: raw.stock ?? 0,
    available: raw.available ?? true,
    rate: raw.rate ?? 0,

    photos: raw.photos ?? [],
    videoUrl: raw.videoUrl ?? "",

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    isReviewed: raw.isReviewed ?? false,
    isDraft: raw.isDraft ?? true,

    joinDate: raw.joinDate ?? new Date().toISOString(),

    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),

    ...safeRaw,
  };
};




export const upsertProduct = createAsyncThunk<
  Product, // First arg: Return type
  {        // Second arg: Thunk arguments
    listingId?: string;
    brandId?: string;
    data: Omit<
      Partial<Product>,
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "photos"
      | "videoUrl"
    >;
  }
>("storeProducts/upsert", async ({ listingId, brandId: brandIdArg, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");
    
    /* ── find brand ── */
    let brandId = brandIdArg;
    if (!brandId) {
      const partnerQuery = query(
        collection(db, "commercePartners"),
        where("userId", "==", user.uid)
      );
      const partnerSnap = await getDocs(partnerQuery);
      if (partnerSnap.empty) {
        throw new Error("No product partner found");
      }
      brandId = partnerSnap.docs[0].id;
    }
    
    /* ── clean data ── */
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );
    const baseData = {
      ...cleanData,
      brandId,
      hostId: user.uid,
      updatedAt: serverTimestamp(),
    };
    
    /* ── update existing ── */
    if (listingId) {
      const refDoc = doc(db, PRODUCTS_COLLECTION, listingId);
      await updateDoc(refDoc, baseData);
      const snap = await getDoc(refDoc);
      return parseProduct(snap.id, snap.data() || {});
    }
    
    /* ── create new ── */
    const payload = {
      ...baseData,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged: false,
      isComplete: false,
      isReviewed: false,
    };
    const colRef = collection(db, PRODUCTS_COLLECTION);
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });
    const snap = await getDoc(newDoc);
    return parseProduct(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});


/* Upload photos or a video for a product */
export const upsertProductMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { files: File[]; listingId?: string; brandId?: string; type: "photo" | "video" },
  { rejectValue: string }
>(
  "storeProducts/upsertMedia",
  async ({ files, listingId, brandId: brandIdArg, type }, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const hostId = currentUser.uid;
      const joinDate = new Date().toISOString();
      let id = listingId;

      /* ── resolve brand (same fallback as upsertProduct) ── */
      let brandId = brandIdArg;
      if (!brandId) {
        const partnerQuery = query(
          collection(db, "commercePartners"),
          where("userId", "==", hostId)
        );
        const partnerSnap = await getDocs(partnerQuery);
        if (partnerSnap.empty) throw new Error("No product partner found");
        brandId = partnerSnap.docs[0].id;
      }

      const uploaded: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const name = file.name || `${type}-${Date.now()}-${i}`;
        const path = `${PRODUCTS_COLLECTION}/${id || "new"}/${type === "photo" ? "photos" : "video"}/${name}`;
        const storageRef = ref(storage, path);

        // Upload to Firebase Storage
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploaded.push(url);
      }

      // ✅ If product doesn't exist yet → create it
      if (!id) {
        const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), {
          hostId,
          brandId,
          joinDate,
          photos: type === "photo" ? uploaded : [],
          videoUrl: type === "video" ? uploaded[0] : "",
          isPublished: false,
          isFlagged: false,
          isComplete: false,
          isReviewed: false,
          inReview: true,
          isDraft: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        id = docRef.id;
        await updateDoc(doc(db, PRODUCTS_COLLECTION, id), { listingId: id });
      } else {
        // ✅ If product exists → update Firestore record
        const updateData =
          type === "photo"
            ? { photos: arrayUnion(...uploaded) }
            : { videoUrl: uploaded[0] };

        await updateDoc(doc(db, PRODUCTS_COLLECTION, id), {
          ...updateData,
          updatedAt: serverTimestamp(),
        });
      }

      return { listingId: id, downloadURLs: uploaded };
    } catch (err: any) {
      console.error("🔥 upsertProductMedia error:", err);
      return rejectWithValue(err.message || "Media upload failed");
    }
  }
);


// 🔥 Universal Delete Media Thunk (handles both photo & video)
export const deleteProductMedia = createAsyncThunk<
  { listingId: string; deletedPhoto?: string; deletedVideo?: boolean },
  { listingId: string; photoUrl?: string; deleteVideo?: boolean },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  "storeProducts/deleteMedia",
  async ({ listingId, photoUrl, deleteVideo }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!listingId) throw new Error("No product specified");

      /* Prefer the doc in state, fall back to a read — the form view keeps
         products in brandProducts, not currentProduct, so neither alone
         is reliable here. */
      const { currentProduct, brandProducts, products } = getState().storeProducts;
      let current: Product | undefined =
        (currentProduct?.listingId === listingId ? currentProduct : undefined) ??
        brandProducts.find((p) => p.listingId === listingId) ??
        products.find((p) => p.listingId === listingId);

      if (!current) {
        const snap = await getDoc(doc(db, PRODUCTS_COLLECTION, listingId));
        if (!snap.exists()) throw new Error("Product not found");
        current = parseProduct(snap.id, snap.data());
      }

      const updates: Record<string, any> = {};
      let deletedPhoto: string | undefined;
      let deletedVideo = false;

      // 🗑️ Delete the photo if provided
      if (photoUrl) {
        try {
          await deleteObject(ref(storage, photoUrl));
        } catch (err) {
          /* already gone, or a stale URL — still drop it from the array */
          console.warn("⚠️ Failed to delete photo from storage:", err);
        }
        updates.photos = (current.photos || []).filter((p) => p !== photoUrl);
        deletedPhoto = photoUrl;
      }

      // 🎬 Delete video only when explicitly asked
      if (deleteVideo && current.videoUrl) {
        try {
          await deleteObject(ref(storage, current.videoUrl));
        } catch (err) {
          console.warn("⚠️ Failed to delete video from storage:", err);
        }
        updates.videoUrl = "";
        deletedVideo = true;
      }

      // 🧾 Update Firestore if anything changed
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, PRODUCTS_COLLECTION, listingId), {
          ...updates,
          updatedAt: serverTimestamp(),
        });
      }

      return { listingId, deletedPhoto, deletedVideo };
    } catch (e: any) {
      console.error("🔥 deleteProductMedia error:", e);
      return rejectWithValue(e.message || "Failed to delete media");
    }
  }
);



/* ───────────────── LISTENERS ───────────────── */

/* Listen to all published products for a specific brand */
export const listenToBrandProducts =
  ({ brandId }: { brandId: string }) =>
  (dispatch: AppDispatch) => {
    if (!brandId) return;

    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("brandId", "==", brandId),
      where("isPublished", "==", true)
    );

    return onSnapshot(q, (snap) => {
      dispatch(
        setProducts(
          snap.docs.map((d) => parseProduct(d.id, d.data()))
        )
      );
    });
  };

/* Listen to all products owned by the current host (seller dashboard) */
export const listenToHostProducts =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().storeProducts;

    /* always clean up the previous listener */
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("hostId", "==", hostId)
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(
        setProducts(
          snap.docs.map((d) => parseProduct(d.id, d.data()))
        )
      );
    });

    dispatch(setUnsubscribe(unsub));
  };

/* Listen to all published products (marketplace feed) */
export const listenToPublishedProducts =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().storeProducts;
    if (unsubscribe) return;

    /* 1️⃣ Listen to published brands first */
    const brandsQuery = query(
      collection(db, "commercePartners"),
      where("isPublished", "==", true)
    );

    const brandsUnsub = onSnapshot(brandsQuery, (brandSnap) => {
      const publishedBrandIds = brandSnap.docs.map((d) => d.id);

      /* no published brands → clear products */
      if (!publishedBrandIds.length) {
        dispatch(setProducts([]));
        return;
      }

      /* 🔥 Firestore "in" limit = 10, so chunk brand IDs */
      const chunkSize = 10;
      const chunks: string[][] = [];
      for (let i = 0; i < publishedBrandIds.length; i += chunkSize) {
        chunks.push(publishedBrandIds.slice(i, i + chunkSize));
      }

      let allProducts: Product[] = [];
      let listeners: (() => void)[] = [];

      chunks.forEach((chunk) => {
        const productsQuery = query(
          collection(db, PRODUCTS_COLLECTION),
          where("isPublished", "==", true),
          where("brandId", "in", chunk)
        );

        const unsubProducts = onSnapshot(productsQuery, (snap) => {
          const products = snap.docs.map((d) =>
            parseProduct(d.id, d.data())
          );

          /* merge without duplicates */
          allProducts = [
            ...allProducts.filter(
              (p) => !products.some((np) => np.listingId === p.listingId)
            ),
            ...products,
          ];

          dispatch(setProducts(allProducts));
        });

        listeners.push(unsubProducts);
      });

      /* save combined unsubscribe */
      dispatch(
        setUnsubscribe(() => {
          brandsUnsub();
          listeners.forEach((u) => u());
        })
      );
    });
  };

/* Listen to a single product document */
export const listenToProductData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;

    const { unsubscribe } = getState().storeProducts;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const refDoc = doc(db, PRODUCTS_COLLECTION, listingId);

    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) {
        dispatch(setCurrentProduct(null));
        return;
      }

      dispatch(setCurrentProduct(parseProduct(snap.id, snap.data())));
    });

    dispatch(setUnsubscribe(unsub));
  };



/* Delete a single product photo */
export const deleteProductPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>(
  "storeProducts/deletePhoto",
  async ({ listingId, photoUrl }) => {
    const refDoc = doc(db, PRODUCTS_COLLECTION, listingId);
    const snap = await getDoc(refDoc);

    if (!snap.exists()) throw new Error("Product not found");

    const photos = snap.data()?.photos || [];

    await updateDoc(refDoc, {
      photos: photos.filter((p: string) => p !== photoUrl),
    });

    if (photoUrl.includes("/o/")) {
      const encoded = photoUrl.split("/o/")[1].split("?")[0];
      await deleteObject(ref(storage, decodeURIComponent(encoded)));
    }

    return photoUrl;
  }
);

/* ───────────────── DELETE ───────────────── */

export const deleteProduct = createAsyncThunk<
  { listingId: string },
  string
>("storeProducts/delete", async (listingId) => {
  await deleteDoc(doc(db, PRODUCTS_COLLECTION, listingId));
  return { listingId };
});

/* ───────────────── CATEGORY LISTENERS ───────────────── */

/* Listen to published products filtered by a single category */
export const listenToProductsByCategory =
  (categoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;

    const { unsubscribe } = getState().storeProducts;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("isPublished", "==", true),
      where("categoryId", "==", categoryId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const products = snap.docs.map((d) =>
          parseProduct(d.id, d.data())
        );

        if (products.length === 0) {
          /* 🔥 auto-fallback to full published feed */
          dispatch(listenToPublishedProducts());
          return;
        }

        dispatch(setProducts(products));
      },
      (error) => {
        dispatch(setError(error.message));
      }
    );

    dispatch(setUnsubscribe(unsub));
  };

/* Listen to published products matching any of the given categories */
export const listenToCategoryGroup =
  (categoryIds: number[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryIds.length) return;

    const { unsubscribe } = getState().storeProducts;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("isPublished", "==", true),
      where("categoryId", "in", categoryIds)
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(
        setProducts(
          snap.docs.map((d) => parseProduct(d.id, d.data()))
        )
      );
    });

    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── FILTERS ───────────────── */

/* Filter currently loaded products by the brand's city/country */
export const filterProductsByBrandLocation =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { products, filters } = getState().storeProducts;
    const { partners } = getState().commercePartners;

    if (!filters.city && !filters.country) {
      dispatch(setFilteredProducts(products));
      return;
    }

    const filtered = products.filter((product) => {
      const brand = partners.find((p) => p.brandId === product.brandId);
      if (!brand) return false;

      const cityMatch = filters.city
        ? brand.address?.city
            ?.toLowerCase()
            .includes(filters.city.toLowerCase())
        : true;

      const countryMatch = filters.country
        ? brand.address?.country
            ?.toLowerCase()
            .includes(filters.country.toLowerCase())
        : true;

      return cityMatch && countryMatch;
    });

    dispatch(setFilteredProducts(filtered));
  };

/* Filter products by country — fetches matching brands from Firestore */
export const filterLocalProductsByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!country) {
      dispatch(setFilteredProducts(getState().storeProducts.products));
      return;
    }

    try {
      /* 1️⃣ fetch brands in that country */
      const partnersQuery = query(
        collection(db, "commercePartners"),
        where("address.country", "==", country)
      );

      const partnersSnap = await getDocs(partnersQuery);
      const localBrandIds = partnersSnap.docs.map((d) => d.id);

      if (!localBrandIds.length) {
        dispatch(setFilteredProducts([]));
        return;
      }

      /* 2️⃣ filter loaded products by matching brandId */
      const { products } = getState().storeProducts;
      const filtered = products.filter((p) =>
        localBrandIds.includes(p.brandId)
      );

      dispatch(setFilteredProducts(filtered));
    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };


// AFTER — storeProductSlice.ts
export const listenToBrandProductsFeed =
  (brandId: string) =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {
    if (!brandId) return () => {};

    const user = getAuth().currentUser;
    if (!user) {
      dispatch(setError("Not authenticated"));
      return () => {};
    }

    dispatch(setBrandProductsLoading(true));
    dispatch(setBrandProductsId(brandId));

    // Query now matches the security rule's provable condition exactly
    // (hostId == request.auth.uid), so Firestore can verify safety from
    // the query alone instead of rejecting the whole listen with
    // permission-denied.
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      where("brandId", "==", brandId),
      where("hostId", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const products: Product[] = snap.docs.map((d) =>
          parseProduct(d.id, d.data())
        );
        dispatch(setBrandProducts(products));
        dispatch(setBrandProductsLoading(false));
      },
      (err) => {
        dispatch(setError(err.message));
        dispatch(setBrandProductsLoading(false));
      }
    );

    dispatch(setBrandUnsubscribe(unsub));
    return unsub;
  };



/* Stop the brand-scoped listener and clear brand products */
export const stopBrandProductsFeed =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { brandUnsubscribe } = getState().storeProducts;
    if (brandUnsubscribe) {
      brandUnsubscribe();
      dispatch(setBrandUnsubscribe(undefined));
      dispatch(setBrandProductsId(null));
      dispatch(clearBrandProducts());
    }
  };

/* ───────────────── SLICE ───────────────── */

const storeProductSlice = createSlice({
  name: "storeProducts",
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.products = action.payload;
    },
    setCurrentProduct: (state, action: PayloadAction<Product | null>) => {
      state.currentProduct = action.payload;
    },
    setFilteredProducts: (state, action: PayloadAction<Product[]>) => {
      state.filteredProducts = action.payload;
    },
    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<{ city?: string | null; country?: string | null }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetProductFilters: (state) => {
      state.filters = { city: null, country: null };
      state.filteredProducts = state.products;
    },
    setBrandProducts: (state, a: PayloadAction<Product[]>) => {
      state.brandProducts = a.payload;
    },
    clearBrandProducts: (state) => {
      state.brandProducts = [];
    },
    setBrandProductsId: (state, a: PayloadAction<string | null>) => {
      state.brandProductsId = a.payload;
    },
    setBrandProductsLoading: (state, a: PayloadAction<boolean>) => {
      state.brandProductsLoading = a.payload;
    },
    setBrandUnsubscribe: (
      state,
      a: PayloadAction<(() => void) | undefined>
    ) => {
      state.brandUnsubscribe = a.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertProduct.fulfilled, (state, action) => {
        const idx = state.products.findIndex(
          (p) => p.listingId === action.payload.listingId
        );
        if (idx !== -1) state.products[idx] = action.payload;
        else state.products.push(action.payload);
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(
          (p) => p.listingId !== action.payload.listingId
        );
      });
  },
});

export const {
  setProducts,
  setCurrentProduct,
  setUnsubscribe,
  setError,
  setFilters,
  setFilteredProducts,
  resetProductFilters,
  setBrandProducts,
  clearBrandProducts,
  setBrandProductsId,
  setBrandProductsLoading,
  setBrandUnsubscribe,
} = storeProductSlice.actions;

export const storeProductsReducer = storeProductSlice.reducer;
export default storeProductsReducer;